import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  IconButton,
  Divider,
  Avatar,
  Chip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as ChecklistIcon,
  Receipt as ExpenseIcon,
  Build as MaintenanceIcon,
  Menu as MenuIcon,
  ExitToApp as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  PersonOutline as UserIcon,
  Settings as SettingsIcon,
  AccountBalance as BankIcon,
  Security as SafetyIcon
} from '@mui/icons-material';

const MobileNavigation = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userViewMode, setUserViewMode] = useState(() => {
    return user?.is_admin ? localStorage.getItem('adminUserViewMode') === 'true' : false;
  });

  // Get current tab based on location
  const getCurrentTab = () => {
    const path = location.pathname;
    if (path === '/') return 0;
    if (path.includes('checklist') || path === '/new-checklist') return 1;
    if (path.includes('expense')) return 2;
    if (path.includes('maintenance')) return 3;
    return 0;
  };

  const handleTabChange = (event, newValue) => {
    const paths = ['/', '/new-checklist', '/expense-report', '/maintenance-invoices'];
    navigate(paths[newValue]);
  };

  const handleViewModeToggle = () => {
    const newMode = !userViewMode;
    setUserViewMode(newMode);
    localStorage.setItem('adminUserViewMode', newMode);
    window.dispatchEvent(new Event('storage'));
    setDrawerOpen(false);
    navigate('/');
  };

  const adminMenuItems = [
    { label: 'Settings', path: '/admin/settings', icon: <SettingsIcon /> },
    { label: 'Maintenance Invoices', path: '/admin/maintenance-invoices', icon: <MaintenanceIcon /> },
    { label: 'Bank Statements', path: '/admin/bank-statements', icon: <BankIcon /> },
    { label: 'Safety Letters', path: '/admin/safety-letters', icon: <SafetyIcon /> },
    { label: 'Expense Reports', path: '/admin/expense-reports', icon: <ExpenseIcon /> }
  ];

  return (
    <>
      {/* Bottom Navigation */}
      <Paper 
        sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          zIndex: 1100,
          paddingBottom: 'env(safe-area-inset-bottom)'
        }} 
        elevation={8}
      >
        <BottomNavigation
          value={getCurrentTab()}
          onChange={handleTabChange}
          sx={{
            height: 70,
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              padding: '8px 12px',
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.75rem',
                '&.Mui-selected': {
                  fontSize: '0.75rem'
                }
              }
            }
          }}
        >
          <BottomNavigationAction
            label="Dashboard"
            icon={<DashboardIcon />}
          />
          <BottomNavigationAction
            label="Checklist"
            icon={<ChecklistIcon />}
          />
          <BottomNavigationAction
            label="Expenses"
            icon={<ExpenseIcon />}
          />
          <BottomNavigationAction
            label="Maintenance"
            icon={<MaintenanceIcon />}
          />
          <BottomNavigationAction
            label="Menu"
            icon={<MenuIcon />}
            onClick={(e) => {
              e.preventDefault();
              setDrawerOpen(true);
            }}
          />
        </BottomNavigation>
      </Paper>

      {/* Menu Drawer */}
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '80vh'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
              {user?.username?.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">{user?.username}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                {user?.is_admin && (
                  <Chip
                    size="small"
                    label={userViewMode ? "User View" : "Admin View"}
                    color={userViewMode ? "secondary" : "primary"}
                    variant="outlined"
                  />
                )}
                <Chip
                  size="small"
                  label={user?.vehicle_number || "No Vehicle"}
                  variant="outlined"
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Admin Menu Items */}
          {user?.is_admin && !userViewMode && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, px: 2 }}>
                Admin Tools
              </Typography>
              <List>
                {adminMenuItems.map((item) => (
                  <ListItem
                    key={item.path}
                    button
                    onClick={() => {
                      navigate(item.path);
                      setDrawerOpen(false);
                    }}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      minHeight: 48
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* View Mode Toggle for Admins */}
          {user?.is_admin && (
            <ListItem
              button
              onClick={handleViewModeToggle}
              sx={{
                borderRadius: 1,
                mb: 1,
                minHeight: 48,
                bgcolor: 'action.hover'
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {userViewMode ? <AdminIcon /> : <UserIcon />}
              </ListItemIcon>
              <ListItemText 
                primary={userViewMode ? "Switch to Admin View" : "Switch to User View"}
              />
            </ListItem>
          )}

          {/* Logout */}
          <ListItem
            button
            onClick={() => {
              logout();
              setDrawerOpen(false);
            }}
            sx={{
              borderRadius: 1,
              minHeight: 48,
              color: 'error.main'
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItem>
        </Box>
      </Drawer>
    </>
  );
};

export default MobileNavigation; 